import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { callApi } from '../services/ApiServices';
import { formatCurrency, formatTimestamp } from '../utlis/function';
import CommonLoader from '../components/common/CommonLoader';
import { ImagePath } from '../components/common/ImagePath';
import ConfirmationModal from '../components/common/ConfirmationModal';
import toast from 'react-hot-toast';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchOrderDetail();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      const response = await callApi({
        method: "get",
        url: `/admin/order-designs/${id}`,
      });
      setOrder(response?.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching order details:", error);
      setLoading(false);
    }
  };

  const downloadContractPdf = async (contractId) => {
  try {
    setPdfLoading(true)
    const res = await callApi({
      method: "get",
      url: `contract/download/${contractId}`,
      responseType: "blob", 
    });
    console.log("download contract", res)
    const url = URL.createObjectURL(res);

    const link = document.createElement("a");
    link.href = url;
    link.download = `contract-${contractId}.pdf`;
    link.click();

    URL.revokeObjectURL(url);
  } catch (error) {
    console.log("Failed to download PDF", error);
  }finally{
    setPdfLoading(false)
  }
};

  const cancelSubscription = async () => {
    try {
      setCancelLoading(true);
      const response = await callApi({
        method: "put",
        url: `orders/cancel/${order?.id}`,
        data: {},
      });
      toast.success(response?.message || "Subscription cancelled successfully");
      setOpenCancelModal(false);
      fetchOrderDetail();
    } catch (error) {
      console.log("error while cancelling subscription", error);
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) return <CommonLoader fullScreen />;

  // Status badge helper (assuming a status exists in data)
  const getStatusBadge = (status) => {
    const baseClass = "px-3 py-1 text-xs font-medium rounded-full";
    switch (status?.toLowerCase()) {
      case 'active': return `${baseClass} bg-green-100 text-green-800`;
      case 'pending': return `${baseClass} bg-yellow-100 text-yellow-800`;
      default: return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  const contractStatus = order?.contract?.status?.toUpperCase();
  const contractStatusLabel = order?.contract?.status || "Not available";

  const addMonthsSafe = (date, months) => {
  const d = new Date(date);
  const day = d.getDate();

  d.setDate(1); 
  d.setMonth(d.getMonth() + months);

  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();

  d.setDate(Math.min(day, lastDay)); 

  return d;
};
const getNextBillingDate = (startDate, endDate) => {
  if (!startDate) return null;
  const today = new Date();
  const start = new Date(startDate);
  let nextBilling = new Date(start)
  const end = endDate ? new Date(endDate): null

   while(nextBilling <= today){
     nextBilling = addMonthsSafe(nextBilling, 1);
   }
   if(end && nextBilling > end){
    return null;
   }

   return nextBilling.toLocaleDateString("en-GB",{
    day:"2-digit",
    month:"short",
    year:"numeric"
   });
};

  return (
    <div className="space-y-8 pb-10">
      {/* 1. Header & Navigation */}
      <div className="flex items-center justify-between gap-4 pb-2 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 bg-white rounded-full shadow-[0px_0px_4px_#6d6d6d4a] hover:bg-gray-50 transition-all active:scale-95"
            title="Go Back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6d6d6d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-black tracking-tight">
              Order {order?.id ? `#${order.id}` : ''}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {order?.status && getStatusBadge(order.status)}
          <span className="text-sm text-gray-500">
            Created: {formatTimestamp(order?.createdAt) || "-"}
          </span>
        </div>
      </div>

      {/* 2. Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 items-start">
        
        {/* Left Column (Info & Designs) */}
        <div className="xl:col-span-3 space-y-8">
          
          {/* Club & Advertiser Overview Card */}
          <div className="bg-white rounded-xl shadow-[0px_0px_4px_#6d6d6d4a] p-7 transition-all hover:shadow-[0px_0px_8px_#6d6d6d60]">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              {/* Club Info */}
              <div className="flex items-center gap-5 flex-1">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 flex-shrink-0 shadow-inner">
                  <img
                    src={order?.club?.clubLogoFile?.fileUrl || 'https://via.placeholder.com/80'}
                    alt="Club Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-black leading-tight">{order?.club?.clubName || "-"}</h2>
                  <p className="text-textcolor flex items-center gap-2 mt-1">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    {order?.club?.contactAddress || "Location not available"}
                  </p>
                </div>
              </div>
              
              <div className="hidden md:block h-16 w-px bg-gray-100"></div>

              {/* Advertiser Info */}
              <div className="flex items-center gap-4 flex-1 md:justify-end">
                <div className="text-left md:text-right">
                  <p className="text-sm text-gray-500">Advertiser</p>
                  <p className="text-lg font-medium text-black">{order?.user?.name || "-"}</p>
                </div>
                <div className="w-14 h-14 rounded-full bg-gray-600 flex items-center justify-center text-white text-xl font-bold uppercase shadow-md">
                  {order?.user?.name ? order.user.name.charAt(0) : 'A'}
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Details Card */}
          <div className="bg-white rounded-[20px] shadow-cardmain overflow-hidden p-4 sm:p-6 space-y-3 sm:space-y-4">
             <div className="flex items-center gap-3 sm:gap-4 text-lg sm:text-xl md:text-[24px] font-semibold text-black">
              {/* <div className="p-1.5 sm:p-2 rounded-lg border border-[#C7C7C7] shadow flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.25885 1.17651H29.0156C29.2023 1.17651 29.3624 1.24318 29.4958 1.3744L33.2976 5.1762C33.4288 5.30961 33.4955 5.46969 33.4955 5.65644V34.0858C33.4955 34.4571 33.1909 34.7616 32.8174 34.7616H8.25885C7.88756 34.7616 7.58301 34.4571 7.58301 34.0858V1.85463C7.58301 1.48114 7.88756 1.17651 8.25885 1.17651Z"
                    fill="#D1D1D6"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M29.2959 1.23657C29.3692 1.26771 29.4337 1.31445 29.496 1.37445L33.2978 5.17626C33.3579 5.23634 33.4045 5.303 33.4357 5.37414H29.6361C29.4493 5.37414 29.2959 5.223 29.2959 5.03618V1.23657Z"
                    fill="#A79BA7"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.19667 5.24072H24.9534C25.1401 5.24072 25.3002 5.30519 25.4314 5.43861L29.2332 9.24049C29.3644 9.37162 29.4311 9.5317 29.4311 9.71845V38.1478C29.4311 38.5213 29.1265 38.8236 28.7552 38.8236H4.19667C3.82318 38.8236 3.51855 38.5213 3.51855 38.1478V5.91664C3.51855 5.54315 3.82318 5.24072 4.19667 5.24072Z"
                    fill="#ECEFF1"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.19667 5.24072H5.78193V38.8236H4.19667C3.82318 38.8236 3.51855 38.5213 3.51855 38.1478V5.91664C3.51855 5.54315 3.82318 5.24072 4.19667 5.24072Z"
                    fill="#D1D1D6"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M25.2334 5.29846C25.307 5.33333 25.3739 5.38073 25.4313 5.43854L29.2331 9.24042C29.291 9.29772 29.3384 9.36469 29.3732 9.43831H25.5713C25.3845 9.43831 25.2334 9.28489 25.2334 9.10034V5.29846Z"
                    fill="#A79BA7"
                  />
                  <path
                    d="M30.8697 26.7678C33.9691 26.7678 36.4817 24.2552 36.4817 21.1559C36.4817 18.0565 33.9691 15.5439 30.8697 15.5439C27.7704 15.5439 25.2578 18.0565 25.2578 21.1559C25.2578 24.2552 27.7704 26.7678 30.8697 26.7678Z"
                    fill="#8AC9FE"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M30.4775 15.5574C30.6081 15.5484 30.7389 15.5439 30.8697 15.5439C33.967 15.5439 36.4817 18.0585 36.4817 21.1559C36.4817 24.2531 33.967 26.7678 30.8697 26.7678C30.7379 26.7678 30.6071 26.7632 30.4775 26.7542C33.3922 26.5527 35.6974 24.1214 35.6974 21.1559C35.6974 18.1904 33.3922 15.759 30.4775 15.5574Z"
                    fill="#60B7FF"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M30.058 35.3896V38.8236C30.058 38.99 29.9918 39.1496 29.8742 39.2673C29.7565 39.3849 29.5969 39.451 29.4305 39.451H3.51905C3.35264 39.451 3.19305 39.3849 3.07538 39.2673C2.95771 39.1496 2.8916 38.99 2.8916 38.8236V5.24131C2.8916 5.0749 2.95771 4.9153 3.07538 4.79763C3.19305 4.67996 3.35264 4.61386 3.51905 4.61386H6.95639V1.17652C6.95639 1.01011 7.02249 0.850518 7.14016 0.732848C7.25783 0.615178 7.41743 0.549072 7.58384 0.549072H29.298C29.3804 0.549069 29.462 0.565311 29.5382 0.596869C29.6143 0.628427 29.6835 0.674683 29.7418 0.732994L33.9391 4.93378C34.0567 5.05143 34.1228 5.21097 34.1227 5.37731V15.8319C35.9132 16.9291 37.1089 18.9039 37.1089 21.1559C37.1089 23.4078 35.9132 25.3827 34.1227 26.4799V34.7622C34.1227 34.9286 34.0566 35.0882 33.939 35.2058C33.8213 35.3235 33.6617 35.3896 33.4953 35.3896H30.058ZM8.21129 4.61386H25.2332C25.398 4.61386 25.558 4.6788 25.6768 4.7977L29.8742 8.99503C29.9325 9.05329 29.9787 9.12247 30.0102 9.1986C30.0417 9.27472 30.058 9.35632 30.058 9.43872V14.9689C30.3271 14.9339 30.5981 14.9164 30.8695 14.9165C31.549 14.9158 32.2241 15.0264 32.8678 15.2439V6.00476H29.298C29.1315 6.00476 28.972 5.93865 28.8543 5.82098C28.7366 5.70331 28.6705 5.54372 28.6705 5.37731V1.80397H8.21129V4.61386ZM24.6057 5.86876H4.1465V38.1961H28.8031V27.0444C26.3737 26.1908 24.6301 23.8753 24.6301 21.1559C24.6301 18.4364 26.3737 16.1209 28.8031 15.2673V10.0662H25.2332C25.0668 10.0662 24.9072 10.0001 24.7895 9.88239C24.6718 9.76472 24.6057 9.60513 24.6057 9.43872V5.86876ZM30.058 27.3429V34.1347H32.8678V27.0678C32.2241 27.2853 31.549 27.3959 30.8695 27.3953C30.5945 27.3953 30.3236 27.3774 30.058 27.3429ZM31.9813 4.74986L29.9254 2.69221V4.74986H31.9813ZM27.9157 8.81127L25.8606 6.75613V8.81127H27.9157ZM30.8695 16.1714C28.1185 16.1714 25.885 18.4048 25.885 21.1559C25.885 23.9069 28.1185 26.1404 30.8695 26.1404C33.6205 26.1404 35.854 23.9069 35.854 21.1559C35.854 18.4048 33.6205 16.1714 30.8695 16.1714ZM30.242 20.5284V18.5301C30.242 18.1837 30.5232 17.9026 30.8695 17.9026C31.2158 17.9026 31.4969 18.1837 31.4969 18.5301V20.5284H33.4953C33.8416 20.5284 34.1227 20.8095 34.1227 21.1559C34.1227 21.5022 33.8416 21.7833 33.4953 21.7833H31.4969V23.7817C31.4969 24.128 31.2158 24.4092 30.8695 24.4092C30.5232 24.4092 30.242 24.128 30.242 23.7817V21.7833H28.2437C27.8974 21.7833 27.6162 21.5022 27.6162 21.1559C27.6162 20.8095 27.8974 20.5284 28.2437 20.5284H30.242ZM8.80737 29.8192C8.46109 29.8192 8.17991 29.5381 8.17991 29.1917C8.17991 28.8455 8.46109 28.5643 8.80737 28.5643H24.1456C24.4918 28.5643 24.773 28.8455 24.773 29.1917C24.773 29.5381 24.4918 29.8192 24.1456 29.8192H8.80737ZM8.80737 34.3394C8.46109 34.3394 8.17991 34.0583 8.17991 33.712C8.17991 33.3657 8.46109 33.0845 8.80737 33.0845H24.1456C24.4918 33.0845 24.773 33.3657 24.773 33.712C24.773 34.0583 24.4918 34.3394 24.1456 34.3394H8.80737ZM8.80737 11.7417C8.46109 11.7417 8.17991 11.4605 8.17991 11.1142C8.17991 10.768 8.46109 10.4868 8.80737 10.4868H19.8157C20.162 10.4868 20.4431 10.768 20.4431 11.1142C20.4431 11.4605 20.162 11.7417 19.8157 11.7417H8.80737ZM8.80737 25.299C8.46109 25.299 8.17991 25.0179 8.17991 24.6715C8.17991 24.3252 8.46109 24.0441 8.80737 24.0441H24.1456C24.4918 24.0441 24.773 24.3252 24.773 24.6715C24.773 25.0179 24.4918 25.299 24.1456 25.299H8.80737ZM8.80737 16.2619C8.46109 16.2619 8.17991 15.9808 8.17991 15.6345C8.17991 15.2881 8.46109 15.007 8.80737 15.007H24.1456C24.4918 15.007 24.773 15.2881 24.773 15.6345C24.773 15.9808 24.4918 16.2619 24.1456 16.2619H8.80737ZM8.80737 20.7787C8.46109 20.7787 8.17991 20.4976 8.17991 20.1513C8.17991 19.805 8.46109 19.5238 8.80737 19.5238H23.0301C23.3765 19.5238 23.6576 19.805 23.6576 20.1513C23.6576 20.4976 23.3765 20.7787 23.0301 20.7787H8.80737Z"
                    fill="black"
                  />
                </svg>
              </div> */}
              Subscription Details
              { order?.orderStatus == 'ACTIVE' && !order?.cancelOn && (
              <button
                onClick={() => setOpenCancelModal(true)}
                className="ml-auto px-4 py-2 text-sm font-medium rounded-full border border-red-500 text-red-600 hover:bg-red-50"
              >
                Cancel Subscription
              </button>
              )}
              {order?.orderStatus == 'ACTIVE' && order?.cancelOn && (
                <span className="ml-auto text-sm font-medium text-red-600">
                  Cancels on: {formatTimestamp(order?.cancelOn)}
                </span>
              )}
              {order?.orderStatus == 'CANCELLED' && (
                <span className="ml-auto text-sm font-medium text-red-600">
                  Cancelled on: {formatTimestamp(order?.cancelledOn)}
                </span>
              )}
            </div>
            <div className="p-7">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-8">
                <DetailItem label="Start Date" value={formatTimestamp(order?.subscriptionStartDate)} icon="calendar" />
                <DetailItem label="End Date" value={formatTimestamp(order?.subscriptionEndDate)} icon="calendar" />
                <DetailItem label="Plan Duration" value={`${order?.subscriptionDuration}  ${Number(order?.subscriptionDuration) === 1 ? "Month" : "Months"}`} icon="clock" />
                <DetailItem label="Display Duration" value={order?.displayDurationSeconds ? `${order?.displayDurationSeconds} sec` : "-"} icon="play" />
                <DetailItem label="Daypart" value={order?.daypart} icon="sun" />
                {/* <DetailItem label="Screens" value={order?.numberOfScreens || "1"} icon="desktop" /> */}
              </div>
            </div>
          </div>

          {/* 3. Uploaded Designs Section (Visual Focus) */}
          {order?.designs?.length > 0 && (
          <div className="bg-white rounded-[20px] shadow-cardmain overflow-hidden p-4 sm:p-6 space-y-3 sm:space-y-4">
            <div className="flex items-center gap-3 sm:gap-4 text-lg sm:text-xl md:text-[24px] font-semibold text-black">
            {/* <div className="p-1.5 sm:p-2 rounded-lg border border-[#C7C7C7] shadow flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
              >
                <g clipPath="url(#clip0_159_3775)">
                  <path
                    d="M1.21191 36.3637C1.21191 37.7031 2.29676 38.7879 3.63616 38.7879H36.3634C37.7028 38.7879 38.7877 37.7031 38.7877 36.3637V7.50305H1.21191V36.3637Z"
                    fill="#D7E6EF"
                  />
                  <path
                    d="M36.3634 1.21216H3.63616C2.29676 1.21216 1.21191 2.29701 1.21191 3.6364V7.50307H38.7877V3.6364C38.7877 2.29701 37.7028 1.21216 36.3634 1.21216Z"
                    fill="#5D5B68"
                  />
                  <path
                    d="M4.84863 11.1394H35.1517V26.6606H4.84863V11.1394Z"
                    fill="#5F6FE7"
                  />
                  <path
                    d="M7.10958 5.56975C7.77902 5.56975 8.3217 5.02707 8.3217 4.35763C8.3217 3.68819 7.77902 3.14551 7.10958 3.14551C6.44015 3.14551 5.89746 3.68819 5.89746 4.35763C5.89746 5.02707 6.44015 5.56975 7.10958 5.56975Z"
                    fill="#E08C7E"
                  />
                  <path
                    d="M11.509 5.56975C12.1784 5.56975 12.7211 5.02707 12.7211 4.35763C12.7211 3.68819 12.1784 3.14551 11.509 3.14551C10.8396 3.14551 10.2969 3.68819 10.2969 4.35763C10.2969 5.02707 10.8396 5.56975 11.509 5.56975Z"
                    fill="#E08C7E"
                  />
                  <path
                    d="M15.9094 5.56975C16.5788 5.56975 17.1215 5.02707 17.1215 4.35763C17.1215 3.68819 16.5788 3.14551 15.9094 3.14551C15.24 3.14551 14.6973 3.68819 14.6973 4.35763C14.6973 5.02707 15.24 5.56975 15.9094 5.56975Z"
                    fill="#E08C7E"
                  />
                  <path
                    d="M7.27288 35.1515C8.61175 35.1515 9.69712 34.0661 9.69712 32.7272C9.69712 31.3883 8.61175 30.303 7.27288 30.303C5.934 30.303 4.84863 31.3883 4.84863 32.7272C4.84863 34.0661 5.934 35.1515 7.27288 35.1515Z"
                    fill="#EE6A51"
                  />
                  <path
                    d="M23.891 35.1515C25.2299 35.1515 26.3153 34.0661 26.3153 32.7272C26.3153 31.3883 25.2299 30.303 23.891 30.303C22.5522 30.303 21.4668 31.3883 21.4668 32.7272C21.4668 34.0661 22.5522 35.1515 23.891 35.1515Z"
                    fill="#EE6A51"
                  />
                  <path
                    d="M8.24863 30.509C8.411 30.8779 8.4788 31.2815 8.4459 31.6832C8.413 32.0849 8.28044 32.4721 8.0602 32.8097C7.83997 33.1473 7.53902 33.4246 7.18461 33.6165C6.8302 33.8085 6.4335 33.909 6.03044 33.909C5.68499 33.909 5.35166 33.8302 5.05469 33.703C5.27899 34.2117 5.67133 34.6278 6.16596 34.8817C6.66058 35.1355 7.22743 35.2117 7.77151 35.0973C8.31558 34.983 8.80382 34.6851 9.1544 34.2536C9.50497 33.8221 9.69658 33.2832 9.69711 32.7272C9.69691 32.2568 9.55986 31.7966 9.30267 31.4028C9.04547 31.0089 8.67924 30.6984 8.24863 30.509ZM24.8668 30.509C25.0292 30.8779 25.097 31.2815 25.0641 31.6832C25.0312 32.0849 24.8986 32.4721 24.6784 32.8097C24.4582 33.1473 24.1572 33.4246 23.8028 33.6165C23.4484 33.8085 23.0517 33.909 22.6486 33.909C22.3032 33.909 21.9698 33.8302 21.6729 33.703C21.8315 34.0638 22.0757 34.3806 22.3844 34.6257C22.6931 34.8709 23.0569 35.0371 23.4443 35.1098C23.8317 35.1826 24.231 35.1598 24.6076 35.0433C24.9842 34.9269 25.3267 34.7204 25.6055 34.4416C25.8842 34.1629 26.0907 33.8204 26.2072 33.4438C26.3236 33.0672 26.3464 32.6679 26.2737 32.2804C26.2009 31.893 26.0347 31.5292 25.7896 31.2205C25.5444 30.9119 25.2277 30.6676 24.8668 30.509Z"
                    fill="#E1533B"
                  />
                  <path
                    d="M27.5338 26.6606C25.2187 24.8909 24.7823 18.6727 21.7156 18.6727C18.655 18.6727 18.2126 24.8909 15.8975 26.6606H27.5338Z"
                    fill="#F8CE01"
                  />
                  <path
                    d="M26.17 24.8424H25.2973C22.1639 24.8424 20.1094 21.4545 21.673 18.7333C21.6851 18.7152 21.6973 18.6909 21.7094 18.6727C18.6609 18.6909 18.2185 24.8909 15.9033 26.6606H27.5336C26.9942 26.2485 26.5579 25.6 26.17 24.8424Z"
                    fill="#F4B601"
                  />
                  <path
                    d="M29.6063 18.9515C31.0489 18.9515 32.2184 17.7821 32.2184 16.3394C32.2184 14.8968 31.0489 13.7273 29.6063 13.7273C28.1636 13.7273 26.9941 14.8968 26.9941 16.3394C26.9941 17.7821 28.1636 18.9515 29.6063 18.9515Z"
                    fill="#D7E6EF"
                  />
                  <path
                    d="M30.7335 13.9939C30.8971 14.3394 31.0001 14.7151 31.0001 15.1212C31.0001 15.8139 30.7249 16.4784 30.2351 16.9682C29.7452 17.4581 29.0808 17.7333 28.388 17.7333C27.982 17.7333 27.6001 17.6303 27.2607 17.4666C27.685 18.3394 28.5698 18.9515 29.6062 18.9515C30.299 18.9515 30.9634 18.6763 31.4532 18.1864C31.9431 17.6965 32.2183 17.0321 32.2183 16.3394C32.2183 15.303 31.6062 14.4181 30.7335 13.9939Z"
                    fill="#A4C0D8"
                  />
                  <path
                    d="M21.8668 26.6606C18.4789 24.0727 17.8365 14.9757 13.3577 14.9757C8.87894 14.9757 8.23651 24.0727 4.84863 26.6606H21.8668Z"
                    fill="#F8CE01"
                  />
                  <path
                    d="M20.3393 24.8424H19.0727C14.0787 24.8424 11.0181 19.2121 13.8787 15.1212C13.8969 15.0909 13.9212 15.0666 13.9393 15.0363C13.7515 15 13.5636 14.9757 13.3636 14.9757C8.88479 14.9757 8.24237 24.0727 4.85449 26.6606H21.8727C21.2848 26.2181 20.7878 25.5818 20.3393 24.8424Z"
                    fill="#F4B601"
                  />
                  <path
                    d="M38.7881 7.95757H1.21236C0.963873 7.95757 0.757812 7.75151 0.757812 7.50302V3.63636C0.757812 2.04848 2.04872 0.757568 3.6366 0.757568H36.3639C37.9518 0.757568 39.2427 2.04848 39.2427 3.63636V7.50302C39.2427 7.75151 39.0366 7.95757 38.7881 7.95757ZM1.6669 7.04848H38.3336V3.63636C38.3336 2.55151 37.4487 1.66666 36.3639 1.66666H3.6366C2.55175 1.66666 1.6669 2.55151 1.6669 3.63636V7.04848Z"
                    fill="black"
                  />
                  <path
                    d="M36.3639 39.2424H3.6366C2.04872 39.2424 0.757812 37.9515 0.757812 36.3636V7.50301C0.757812 7.25452 0.963873 7.04846 1.21236 7.04846H38.7881C39.0366 7.04846 39.2427 7.25452 39.2427 7.50301V36.3636C39.2427 37.9515 37.9518 39.2424 36.3639 39.2424ZM1.6669 7.95755V36.3636C1.6669 37.4485 2.55175 38.3333 3.6366 38.3333H36.3639C37.4487 38.3333 38.3336 37.4485 38.3336 36.3636V7.95755H1.6669Z"
                    fill="black"
                  />
                  <path
                    d="M35.1521 27.1152H4.84908C4.60059 27.1152 4.39453 26.9091 4.39453 26.6606V11.1394C4.39453 10.8909 4.60059 10.6849 4.84908 10.6849H35.1521C35.4006 10.6849 35.6067 10.8909 35.6067 11.1394V26.6606C35.6067 26.9091 35.4006 27.1152 35.1521 27.1152ZM5.30362 26.2061H34.6976V11.5939H5.30362V26.2061ZM7.10968 6.02425C6.18847 6.02425 5.44302 5.2788 5.44302 4.35758C5.44302 3.43637 6.18847 2.69092 7.10968 2.69092C8.03089 2.69092 8.77635 3.43637 8.77635 4.35758C8.77635 5.2788 8.03089 6.02425 7.10968 6.02425ZM7.10968 3.60001C6.90876 3.60001 6.71607 3.67982 6.574 3.8219C6.43192 3.96397 6.35211 4.15666 6.35211 4.35758C6.35211 4.55851 6.43192 4.7512 6.574 4.89327C6.71607 5.03534 6.90876 5.11516 7.10968 5.11516C7.3106 5.11516 7.5033 5.03534 7.64537 4.89327C7.78744 4.7512 7.86726 4.55851 7.86726 4.35758C7.86726 4.15666 7.78744 3.96397 7.64537 3.8219C7.5033 3.67982 7.3106 3.60001 7.10968 3.60001ZM11.5097 6.02425C10.5885 6.02425 9.84302 5.2788 9.84302 4.35758C9.84302 3.43637 10.5885 2.69092 11.5097 2.69092C12.4309 2.69092 13.1763 3.43637 13.1763 4.35758C13.1763 5.2788 12.4248 6.02425 11.5097 6.02425ZM11.5097 3.60001C11.3088 3.60001 11.1161 3.67982 10.974 3.8219C10.8319 3.96397 10.7521 4.15666 10.7521 4.35758C10.7521 4.55851 10.8319 4.7512 10.974 4.89327C11.1161 5.03534 11.3088 5.11516 11.5097 5.11516C11.7106 5.11516 11.9033 5.03534 12.0454 4.89327C12.1874 4.7512 12.2673 4.55851 12.2673 4.35758C12.2673 4.15666 12.1874 3.96397 12.0454 3.8219C11.9033 3.67982 11.7106 3.60001 11.5097 3.60001ZM15.9097 6.02425C14.9885 6.02425 14.243 5.2788 14.243 4.35758C14.243 3.43637 14.9885 2.69092 15.9097 2.69092C16.8309 2.69092 17.5764 3.43637 17.5764 4.35758C17.5764 5.2788 16.8248 6.02425 15.9097 6.02425ZM15.9097 3.60001C15.7088 3.60001 15.5161 3.67982 15.374 3.8219C15.2319 3.96397 15.1521 4.15666 15.1521 4.35758C15.1521 4.55851 15.2319 4.7512 15.374 4.89327C15.5161 5.03534 15.7088 5.11516 15.9097 5.11516C16.1106 5.11516 16.3033 5.03534 16.4454 4.89327C16.5874 4.7512 16.6673 4.55851 16.6673 4.35758C16.6673 4.15666 16.5874 3.96397 16.4454 3.8219C16.3033 3.67982 16.1106 3.60001 15.9097 3.60001ZM7.27332 35.6061C5.68544 35.6061 4.39453 34.3152 4.39453 32.7273C4.39453 31.1394 5.68544 29.8485 7.27332 29.8485C8.8612 29.8485 10.1521 31.1394 10.1521 32.7273C10.1521 34.3152 8.8612 35.6061 7.27332 35.6061ZM7.27332 30.7576C6.18847 30.7576 5.30362 31.6424 5.30362 32.7273C5.30362 33.8121 6.18847 34.697 7.27332 34.697C8.35817 34.697 9.24302 33.8121 9.24302 32.7273C9.24302 31.6424 8.35817 30.7576 7.27332 30.7576ZM18.5339 31.9697H12.037C11.7885 31.9697 11.5824 31.7636 11.5824 31.5152C11.5824 31.2667 11.7885 31.0606 12.037 31.0606H18.5339C18.7824 31.0606 18.9885 31.2667 18.9885 31.5152C18.9885 31.7636 18.7824 31.9697 18.5339 31.9697ZM15.9097 34.3939H12.037C11.7885 34.3939 11.5824 34.1879 11.5824 33.9394C11.5824 33.6909 11.7885 33.4849 12.037 33.4849H15.9097C16.1582 33.4849 16.3642 33.6909 16.3642 33.9394C16.3642 34.1879 16.1582 34.3939 15.9097 34.3939ZM23.8915 35.6061C22.3036 35.6061 21.0127 34.3152 21.0127 32.7273C21.0127 31.1394 22.3036 29.8485 23.8915 29.8485C25.4794 29.8485 26.7703 31.1394 26.7703 32.7273C26.7703 34.3152 25.4794 35.6061 23.8915 35.6061ZM23.8915 30.7576C22.8067 30.7576 21.9218 31.6424 21.9218 32.7273C21.9218 33.8121 22.8067 34.697 23.8915 34.697C24.9763 34.697 25.8612 33.8121 25.8612 32.7273C25.8612 31.6424 24.9763 30.7576 23.8915 30.7576ZM35.1521 31.9697H28.6551C28.4067 31.9697 28.2006 31.7636 28.2006 31.5152C28.2006 31.2667 28.4067 31.0606 28.6551 31.0606H35.1521C35.4006 31.0606 35.6067 31.2667 35.6067 31.5152C35.6067 31.7636 35.4006 31.9697 35.1521 31.9697ZM32.5279 34.3939H28.6551C28.4067 34.3939 28.2006 34.1879 28.2006 33.9394C28.2006 33.6909 28.4067 33.4849 28.6551 33.4849H32.5279C32.7764 33.4849 32.9824 33.6909 32.9824 33.9394C32.9824 34.1879 32.7764 34.3939 32.5279 34.3939Z"
                    fill="black"
                  />
                  <path
                    d="M21.8671 27.1151H4.84893C4.65499 27.1151 4.47923 26.9879 4.41863 26.806C4.35802 26.6242 4.41863 26.4182 4.5762 26.2969C5.89136 25.2909 6.81863 23.0969 7.7156 20.9757C9.06105 17.8 10.4489 14.5151 13.358 14.5151C16.2671 14.5151 17.6611 17.8 19.0004 20.9757C19.8974 23.0969 20.8247 25.2909 22.1398 26.2969C22.2974 26.4182 22.358 26.6182 22.2974 26.806C22.2368 26.9879 22.0611 27.1151 21.8671 27.1151ZM5.96408 26.206H20.7459C19.6914 24.9515 18.9156 23.1212 18.1641 21.3333C16.8792 18.2969 15.6671 15.4303 13.358 15.4303C11.0489 15.4303 9.83681 18.2969 8.55196 21.3333C7.79439 23.1212 7.01863 24.9515 5.96408 26.206ZM29.6065 19.406C27.9156 19.406 26.5398 18.0303 26.5398 16.3394C26.5398 14.6485 27.9156 13.2727 29.6065 13.2727C31.2974 13.2727 32.6732 14.6485 32.6732 16.3394C32.6732 18.0303 31.2974 19.406 29.6065 19.406ZM29.6065 14.1818C28.4186 14.1818 27.4489 15.1515 27.4489 16.3394C27.4489 17.5273 28.4186 18.4969 29.6065 18.4969C30.7944 18.4969 31.7641 17.5273 31.7641 16.3394C31.7641 15.1515 30.7944 14.1818 29.6065 14.1818Z"
                    fill="black"
                  />
                  <path
                    d="M27.5334 27.1151C27.4364 27.1151 27.3395 27.0848 27.2607 27.0242C26.2001 26.2121 25.5273 24.6181 24.8728 23.0787C24.0122 21.0484 23.2061 19.1272 21.7213 19.1272C20.7576 19.1272 20.0182 19.8787 19.1576 21.7272C19.1323 21.7814 19.0964 21.8301 19.0522 21.8704C19.008 21.9107 18.9562 21.9419 18.8999 21.9622C18.8436 21.9825 18.7839 21.9914 18.7241 21.9885C18.6643 21.9857 18.6057 21.971 18.5516 21.9454C18.4969 21.9206 18.4478 21.885 18.4071 21.8409C18.3664 21.7967 18.335 21.7449 18.3147 21.6884C18.2943 21.6319 18.2855 21.5719 18.2887 21.512C18.2919 21.452 18.3071 21.3933 18.3334 21.3394C19.0546 19.7939 20.0182 18.2181 21.7213 18.2181C23.8061 18.2181 24.7758 20.5091 25.7092 22.7212C26.3152 24.1515 26.9395 25.6363 27.8122 26.2969C28.0122 26.4484 28.0485 26.7333 27.897 26.9333C27.8061 27.0545 27.6728 27.1151 27.5334 27.1151Z"
                    fill="black"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_159_3775">
                    <rect width="40" height="40" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </div> */}
            Design Uploaded
          </div>
            
            {order?.designs?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5 mt-5">
                {order.designs?.map((design, index) => (
                  <div key={index} className="group relative aspect-[3/4] rounded-xl overflow-hidden border border-gray-200 bg-gray-50 shadow-sm transition-all hover:border-gray-300 hover:shadow-md">
                    <img 
                      src={design?.fileUrl} 
                      alt={`Design ${index + 1}`} 
                      className="w-full h-full object-cover transition-transform duration-500 "
                    />
            
                  </div>
                ))
                }
              </div>
            ) : (
              <div className="py-12 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-100 text-center">
                <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                <p className="text-xl font-medium text-gray-500">No designs uploaded</p>
                <p className="text-textcolor mt-1">The advertiser has not submitted any creatives yet.</p>
              </div>
            )}
          </div>
            
          )}
           <div className="mt-3 md:mt-5">
          <div className="bg-white rounded-[20px] shadow-cardmain overflow-hidden p-4 sm:p-6 space-y-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg sm:text-xl md:text-[24px] font-semibold text-black">
                  Contract Details
                </h2>
              
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
           
                {contractStatus === "SIGNED" && (
                  <button
                    type="button"
                    onClick={()=>downloadContractPdf(order?.contract?.contractId)}
                    disabled={pdfLoading}
                    className="h-[46px] rounded-full bg-primary px-5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-secondary disabled:cursor-not-allowed disabled:bg-gray-500 cursor-pointer"
                  >
                  { pdfLoading ? "Downloading..." : "Download Contract" }
                  </button>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-6">
              <ContractInfoCard
                label="Contract ID"
                value={order?.contract?.contractId || order?.contractId || "-"}
              />
              <ContractInfoCard
                label="Contract Status"
                value={contractStatusLabel}
                highlighted={Boolean(order?.contract?.status)}
              />
              <ContractInfoCard
                label="Signing Link"
                value={order?.contract?.signingUrl ? "Available" : "Not available yet"}
              />
              <ContractInfoCard
                label="Sent At"
                value={order?.contract?.sentAt ? formatTimestamp(order.contract.sentAt) : "-"}
              />
              <ContractInfoCard
                label="Signed At"
                value={order?.contract?.signedAt ? formatTimestamp(order.contract.signedAt) : "-"}
              />
              <ContractInfoCard
                label="Created At"
                value={order?.contract?.createdAt ? formatTimestamp(order.contract.createdAt) : "-"}
              />
              {/* <ContractInfoCard
              label="Next billing date"
              value = { getNextBillingDate(order?.subscriptionStartDate, order?.subscriptionEndDate) || "-" }
              /> */}
            </div>

            {order?.contract?.failureReason && (
              <div className="rounded-[16px] border border-[#ffd9d6] bg-[#fff7f6] p-4">
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#b42318]">
                  Failure Reason
                </p>
                <p className="mt-2 text-sm font-medium text-[#7a271a]">
                  {order?.contract.failureReason}
                </p>
              </div>
            )}

           {/* {!order?.contract?.signingUrl && (
              <p className="text-sm text-gray-500">
                Contract document is not available yet. Please refresh after a moment if it was just generated.
              </p>
            )} */}
          </div>
        </div>

{/* 
           <div className="mt-3 md:mt-5 ">
          <div className="bg-white rounded-[20px] shadow-cardmain overflow-hidden space-y-2">
            <div className="flex items-center gap-3 sm:gap-4 text-lg sm:text-xl md:text-[24px] font-semibold text-black p-4 sm:p-6">
              <div className="p-1.5 sm:p-2 rounded-lg border border-[#C7C7C7] shadow flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                >
                  <path
                    d="M29.9997 39.3334H9.33301C8.22851 39.3334 7.33301 38.4379 7.33301 37.3334V23.3334H3.33301V2.66675C3.33301 1.56225 4.22851 0.666748 5.33301 0.666748H27.333C28.8057 0.666748 29.9997 1.86075 29.9997 3.33341V39.3334Z"
                    fill="#FBEDBF"
                  />
                  <path
                    d="M17.333 19.3333V18L13.333 21.3333L17.333 24.6667V23.3333H36.6663V19.3333H17.333Z"
                    fill="#99D8AA"
                  />
                  <path
                    d="M9.33301 39.3333C10.4375 39.3333 11.333 38.4378 11.333 37.3333V35.3333H31.333C32.4375 35.3333 33.333 36.2288 33.333 37.3333C33.333 38.4378 32.4375 39.3333 31.333 39.3333H9.33301Z"
                    fill="#FBD699"
                  />
                  <path
                    d="M7.33301 23.3334H3.33301V2.66675C3.33301 1.56225 4.22851 0.666748 5.33301 0.666748C6.43751 0.666748 7.33301 1.56225 7.33301 2.66675V23.3334Z"
                    fill="#F7BA61"
                  />
                  <path
                    d="M10.667 4H17.3337V5.33333H10.667V4ZM10.667 6.66667H17.3337V8H10.667V6.66667ZM13.3337 29.3333H26.667V30.6667H13.3337V29.3333ZM13.3337 32H26.667V33.3333H13.3337V32ZM22.667 26.6667H26.667V28H22.667V26.6667Z"
                    fill="#EEE1B5"
                  />
                  <path
                    d="M24.0003 13.3333L20.0003 10V11.3333H0.666992V15.3333H20.0003V16.6667L24.0003 13.3333Z"
                    fill="#4799CF"
                  />
                  <path
                    d="M36.6667 18.6667H30.6667V3.33333C30.6644 1.49317 29.1735 0.00225 27.3333 0H5.33333C3.86067 0 2.66667 1.194 2.66667 2.66667V10.6667H0.666667C0.489856 10.6667 0.320286 10.7369 0.195262 10.8619C0.0702379 10.987 0 11.1565 0 11.3333L0 15.3333C0 15.5101 0.0702379 15.6797 0.195262 15.8047C0.320286 15.9298 0.489856 16 0.666667 16H2.66667V23.3333C2.66667 23.5101 2.7369 23.6797 2.86193 23.8047C2.98695 23.9298 3.15652 24 3.33333 24H6.66667V37.3333C6.66667 38.806 7.86067 40 9.33333 40H31.3333C32.806 40 34 38.806 34 37.3333C34 35.8607 32.806 34.6667 31.3333 34.6667H30.6667V24H36.6667C36.8435 24 37.0131 23.9298 37.1381 23.8047C37.2631 23.6797 37.3333 23.5101 37.3333 23.3333V19.3333C37.3333 19.1565 37.2631 18.987 37.1381 18.8619C37.0131 18.7369 36.8435 18.6667 36.6667 18.6667ZM4 2.66667C4 1.93033 4.597 1.33333 5.33333 1.33333C6.06967 1.33333 6.66667 1.93033 6.66667 2.66667V10.6667H4V2.66667ZM1.33333 12H20C20.1621 12.0001 20.3187 11.9411 20.4404 11.8341C20.5622 11.727 20.6408 11.5793 20.6615 11.4186L22.9586 13.3333L20.6614 15.2481C20.6407 15.0873 20.5621 14.9397 20.4404 14.8326C20.3186 14.7256 20.1621 14.6666 20 14.6667H1.33333V12ZM4 22.6667V16H6.66667V22.6667H4ZM32.6667 37.3333C32.6667 38.0697 32.0697 38.6667 31.3333 38.6667H11.6419C11.8772 38.2618 12.0008 37.8016 12 37.3333V36H31.3333C32.0697 36 32.6667 36.597 32.6667 37.3333ZM29.3333 34.6667H11.3333C11.1565 34.6667 10.987 34.7369 10.8619 34.8619C10.7369 34.987 10.6667 35.1565 10.6667 35.3333V37.3333C10.6667 38.0697 10.0697 38.6667 9.33333 38.6667C8.597 38.6667 8 38.0697 8 37.3333V16H19.3333V16.6667C19.3333 16.7935 19.3694 16.9177 19.4374 17.0247C19.5055 17.1317 19.6026 17.2171 19.7174 17.2708C19.8323 17.3246 19.9601 17.3445 20.0858 17.3282C20.2116 17.3119 20.3301 17.26 20.4274 17.1788L24.4274 13.8454C24.5024 13.7828 24.5628 13.7045 24.6042 13.616C24.6456 13.5275 24.667 13.431 24.667 13.3333C24.667 13.2356 24.6456 13.1391 24.6042 13.0506C24.5628 12.9622 24.5024 12.8838 24.4274 12.8212L20.4274 9.48792C20.3301 9.40661 20.2116 9.35475 20.0858 9.33842C19.9601 9.32209 19.8323 9.34198 19.7174 9.39574C19.6025 9.4495 19.5054 9.53491 19.4374 9.64194C19.3693 9.74897 19.3332 9.87318 19.3333 10V10.6667H8V2.66667C7.99768 2.19726 7.87012 1.73698 7.6305 1.33333H27.3333C28.4378 1.33333 29.3333 2.22883 29.3333 3.33333V18.6667H18V18C18.0001 17.8732 17.9639 17.749 17.8959 17.642C17.8279 17.535 17.7307 17.4496 17.6159 17.3958C17.501 17.3421 17.3733 17.3222 17.2475 17.3385C17.1217 17.3548 17.0033 17.4066 16.9059 17.4879L12.9059 20.8213C12.8309 20.8839 12.7706 20.9622 12.7292 21.0506C12.6878 21.1391 12.6663 21.2356 12.6663 21.3333C12.6663 21.431 12.6878 21.5275 12.7292 21.616C12.7706 21.7045 12.8309 21.7828 12.9059 21.8454L16.9059 25.1787C17.0032 25.2601 17.1217 25.3119 17.2475 25.3282C17.3733 25.3446 17.5011 25.3247 17.6159 25.2709C17.7308 25.2172 17.8279 25.1318 17.896 25.0247C17.964 24.9177 18.0001 24.7935 18 24.6667V24H29.3333V34.6667ZM36 22.6667H17.3333C17.2475 22.667 17.1625 22.6843 17.0833 22.7175C17.0589 22.7302 17.0353 22.7445 17.0127 22.7601C16.9647 22.7848 16.9199 22.8152 16.8792 22.8506C16.8577 22.8748 16.8379 22.9001 16.82 22.9268C16.7875 22.9643 16.7598 23.0053 16.7367 23.0495C16.7236 23.081 16.7134 23.1136 16.7061 23.1468C16.6921 23.1793 16.681 23.2132 16.6732 23.2481L14.3747 21.3333L16.6718 19.4186C16.6797 19.4534 16.6908 19.4873 16.7048 19.5198C16.7119 19.5534 16.7223 19.5859 16.7353 19.6172C16.7582 19.6614 16.7862 19.7025 16.8187 19.7399C16.8366 19.7666 16.8564 19.792 16.8779 19.8161C16.9186 19.8516 16.9636 19.8818 17.0114 19.9066C17.0342 19.9222 17.0576 19.9366 17.082 19.9493C17.1616 19.9828 17.247 20 17.3333 20H36V22.6667Z"
                    fill="black"
                  />
                  <path
                    d="M10.667 4H17.3337V5.33333H10.667V4ZM10.667 6.66667H17.3337V8H10.667V6.66667ZM13.3337 29.3333H26.667V30.6667H13.3337V29.3333ZM13.3337 32H26.667V33.3333H13.3337V32ZM22.667 26.6667H26.667V28H22.667V26.6667Z"
                    fill="black"
                  />
                </svg>
              </div>
              Transaction History
            </div>
            <div className="w-full">
              <div className="bg-white overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-[#F0F2F2] rounded-2xl">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 text-left text-xs sm:text-sm md:text-[16px] font-medium text-gray-custom uppercase tracking-wider"
                      >
                        TRANSACTION ID
                      </th>
                      <th
                        scope="col"
                        className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 text-left text-xs sm:text-sm md:text-[16px] font-medium text-gray-custom uppercase tracking-wider"
                      >
                       DATE
                      </th>
                    
                      <th
                        scope="col"
                        className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 text-left text-xs sm:text-sm md:text-[16px] font-medium text-gray-custom uppercase tracking-wider"
                      >
                        AMOUNT
                      </th>
                      
                      <th
                        scope="col"
                        className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 text-left text-xs sm:text-sm md:text-[18px] font-medium text-gray-custom uppercase tracking-wider"
                      >
                       PURPOSE
                      </th>
                      <th
                        scope="col"
                        className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 text-left text-xs sm:text-sm md:text-[18px] font-medium text-black uppercase tracking-wider"
                      >
                        TRANSACTION STATUS
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {order?.transaction?.length > 0 ? (
                      order?.transaction?.map((transaction, index) => {
                         return (
                        <tr  key={transaction?.id}>
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm md:text-base lg:text-[18px] font-medium text-black">
                            {transaction?.id
                              ? `#${transaction?.id}`
                              : "-"}
                          </td>
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm md:text-base lg:text-[18px] text-black">
                            {transaction?.createdAt
                              ? formatTimestamp(transaction?.createdAt)
                              : "-"}
                          </td>
                       
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm md:text-base lg:text-[18px] text-black">
                            {transaction?.amount
                              ? `$${transaction?.amount}`
                              : "-"}
                          </td>
                         
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm md:text-base lg:text-[18px]">
                            <div className="flex gap-2">
                              {
                                transaction?.paymentStatus  || "-"}
                            </div>
                          </td>
                          <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                            <button    >                         
                            {transaction?.transactionStatus || "-"}
                            </button>
                          </td>
                        </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          <div className="flex justify-center items-center">
                            <img
                              src={ImagePath?.NoDataFound}
                              alt="No Data Found"
                              style={{ width: 70, height: 70 }}
                            />
                          </div>
                          <p className="text-md mt-2 text-center">
                            No Data found
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div> */}

        </div>


        {/* Right Column (Payment Sidebar) */}
        <div className="xl:col-span-1 xl:sticky xl:top-6">
          <div className="bg-white rounded-xl shadow-[0px_0px_4px_#6d6d6d4a] overflow-hidden transition-all hover:shadow-[0px_0px_8px_#6d6d6d60]">
            <div className="px-6 py-5 border-b border-gray-200 bg-gray-100">
              <h3 className="text-[24px] font-semibold text-black flex items-center gap-3 text-2xl">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                Payment Summary
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <PriceRow label="Subscription Fee" value={order?.totalSubscriptionFee} />
              <PriceRow label="Geo Target Fee" value={order?.geoTargetFee} />
              <PriceRow label="Geo target Rec. Fee" value={order?.geoTargetRecFee} />
              <PriceRow label="Design Fee" value={order?.designFee} />
              
              <div className="border-t border-gray-100 pt-5 mt-5">
                <div className="flex justify-between items-center bg-primary p-4 rounded-lg border border-gray-100">
                  <span className="font-semibold text-white">Total Amount</span>
                  <span className="font-bold text-white text-2xl tracking-tight">
                    {formatCurrency(order?.totalAmount) || "$0.00"}
                  </span>
                </div>
                
              </div>
            </div>
          </div>
        </div>


      </div>
      <ConfirmationModal
        isOpen={openCancelModal}
        title="Cancel Subscription"
        description="Are you sure you want to cancel this subscription? This action cannot be undone."
        confirmText="Yes, Cancel"
        cancelText="No"
        onConfirm={cancelSubscription}
        onCancel={() => setOpenCancelModal(false)}
        isLoading={cancelLoading}
      />
    </div>
  );
};

// --- Polished Helper Components ---

const DetailItem = ({ label, value, icon }) => {
  // Simple icon mapper based on string
  const getIcon = (iconName) => {
    const cls = "w-5 h-5 text-gray-400 flex-shrink-0";
    switch(iconName) {
      case 'calendar': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
      case 'clock': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'play': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'sun': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
      case 'desktop': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
      default: return null;
    }
  };

  return (
    <div className="flex items-start gap-3 bg-gray-100 p-3 rounded-lg border border-gray-100">
      {icon && getIcon(icon)}
      <div>
        <p className="text-xs tracking-wider uppercase text-[16px] font-medium text-gray-custom">{label}</p>
        <p className="text-base font-semibold text-black mt-0.5">{value || "-"}</p>
      </div>
    </div>
  );
};

const PriceRow = ({ label, value }) => (
  <div className="flex justify-between items-center text-sm py-1">
    <span className="text-[16px] font-medium text-gray-custom">{label}</span>
    <span className="text-black font-semibold text-base">{formatCurrency(value) || "$0.00"}</span>
  </div>
);

const ContractInfoCard = ({ label, value, highlighted = false }) => {
  //   const getIcon = (iconName) => {
  //   const cls = "w-5 h-5 text-gray-400 flex-shrink-0";
  //   switch(iconName) {
  //     case 'calendar': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
  //     case 'clock': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
  //     case 'play': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
  //     case 'sun': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
  //     case 'desktop': return <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
  //     default: return null;
  //   }
  // };
  const getContratIcon = (iconName)=>{
    const cls = "w-5 h-5 text-gray-400 flex-shrink-0";
    switch(iconName){
      
    }
  }
  return (
  <div className=" bg-gray-100 p-3 rounded-lg border border-gray-100 p-4">
    <p className="text-xs tracking-wider uppercase text-[16px] font-medium text-gray-custom">{label}</p>
    <p className={`mt-2 text-sm font-semibold ${highlighted ? "text-primary" : "text-black"}`}>
      {value}
    </p>
  </div>
  )
};

export default OrderDetail;